import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress
} from "@mui/material";
import {
  setAgentBuyDialogOpen,
  buyAgentIDPass,
  agentBuySuccessDialogOpen
} from "../../redux/slices/DMA";
import { ReactComponent as RefreshIcon } from "../../assets/icons/refresh.svg";
import AgentCardImage from "../../assets/icons/dashboard/agent-card.png";

import CustomDialog from "./Dialog";
import { displayNum } from "../../utils/strings";
import { useHistory } from "react-router-dom";
import ConfirmationDialog from "./ConfirmationDialog";

function AgentBuyConfirmDialog({ open, setClose, confirmHandler }) {
  return (
    <ConfirmationDialog open={open} width="470px" closeDialog={setClose}>
      <Box>
        <Typography width={270} mt={"20px"} fontSize={{ xxs: 14, sm: 16 }} textAlign="center">
          You are about to purchase Agent ID for 100 AGI, please confirm to proceed the payment.
        </Typography>
        <Box display="flex" justifyContent="center" mt={{ xxs: 2.5, sm: 3 }} mb={1}>
          <Button
            sx={{
              bgcolor: "rgba(255, 135, 54, 1)",
              color: "#000",
              width: "270px",
              height: "50px",
              transition: "all 0.3s",
              fontSize: "18px",
              fontFamily: "Poppins",
              marginTop: "50px",
              "&:hover": {
                bgcolor: "rgba(255, 135, 54, 0.9)"
              }
            }}
            onClick={confirmHandler}>
            CONFIRM
          </Button>
        </Box>
      </Box>
    </ConfirmationDialog>
  );
}

function AgentBuyDialog({ open, setClose }) {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { balance: delysiumBalance, transferProcessing } = useSelector((state) => state.DMA);
  const { userInfo } = useSelector((state) => state.auth);
  const [amount, setAmount] = useState(0);
  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isShowBind2FAConfirm, setIsShowBind2FAConfirm] = useState(false);
  const [isShowBindEmailConfirm, setIsShowBindEmailConfirm] = useState(false);
  const [isShowVerifyDialog, setIsShowVerifyDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedCurrency] = useState("native");
  const [transferType, setTransferType] = useState("deposit");
  const [isSwapped, setIsSwapped] = useState(false);

  const balance = useMemo(() => {
    return userInfo?.dely_balance_agi || 0;
  }, [userInfo]);

  const handleJoin = () => {};

  const handleCoreButtonClick = async () => {
    if (+balance < 100) {
      // 跳转到充值页面
      push("/trade?type=deposit");
      return;
    }

    setIsShowConfirm(true);
  };

  const handleBuy = async () => {
    try {
      await dispatch(buyAgentIDPass());
      setIsShowConfirm(false);
    } catch (error) {
      console.error("Failed to buy Agent ID Pass:", error);
      setIsShowConfirm(false);
    }
  };

  const handleClose = (event, reason) => {
    // if (reason === "backdropClick") {
    //   return;
    // }
    setClose();
  };

  return (
    <CustomDialog
      hasCloseButton={true}
      BackdropProps={{
        sx: {
          backgroundColor: (theme) => alpha(theme.palette.background.default, 0.7)
        }
      }}
      setClose={handleClose}
      open={open}>
      <Box
        sx={{
          display: "flex",
          gap: 4,
          p: 4,
          bgcolor: "black",
          color: "white"
        }}>
        {/* 左侧 3D 卡片 */}
        <Box
          sx={{
            "& img": {
              width: "360px",
              height: "360px",
              objectFit: "contain"
            }
          }}>
          <Box
            component="img"
            src={AgentCardImage}
            sx={{
              border: "0.5px solid rgba(255, 255, 255, 0.2)"
            }}></Box>
        </Box>

        {/* 右侧信息区 */}
        <Box sx={{ flex: 1, width: "270px" }}>
          <Typography
            variant="h4"
            sx={{
              color: "white",
              fontWeight: "400",
              fontSize: "24px",
              fontFamily: "Canva Sans Sans",
              mb: "16px"
            }}>
            AGENT ID
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "#FF8736",
              fontSize: "20px",
              fontWeight: "500",
              fontFamily: "Poppins",
              mb: "16px"
            }}>
            100 AGI
          </Typography>

          <Typography
            sx={{
              lineHeight: 1.6,
              color: "#BABABA",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontWeight: "400",
              height: "173px"
            }}>
            Your AI agent represents you in Delysium ONE, unlocking autonomous collaboration among
            AI agents. Deploying each AI agent requires 100 $AGI tokens and creates a unique Agent
            ID ending with "AGI," directly linking to your Delysium account.
          </Typography>

          <Box sx={{ mt: "auto" }}>
            <Box spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: "10px",
                  fontFamily: "Poppins",
                  fontSize: "14px"
                }}>
                <Typography color="rgba(255, 255, 255, 0.7)" sx={{ fontFamily: "Poppins" }}>
                  Account Balance
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "14px"
                  }}>
                  <Typography sx={{ fontFamily: "Poppins", fontSize: "14px" }}>
                    {displayNum(balance)} AGI
                  </Typography>
                  <RefreshIcon />
                </Box>
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={handleCoreButtonClick}
                sx={{
                  bgcolor: "rgba(255, 135, 54, 1)",
                  color: "#000",
                  width: "270px",
                  height: "50px",
                  transition: "all 0.3s",
                  fontSize: "18px",
                  fontFamily: "Poppins",
                  "&:hover": {
                    bgcolor: "rgba(255, 135, 54, 0.9)"
                  }
                }}>
                {+balance < 100 ? "DEPOSIT" : "JOIN NOW"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <AgentBuyConfirmDialog
        open={isShowConfirm}
        setClose={() => setIsShowConfirm(false)}
        confirmHandler={() => handleBuy()}
      />
    </CustomDialog>
  );
}

export default AgentBuyDialog;
